import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { clearBusinessIdCache } from '@/lib/supabaseDb';
import type { User, Session } from '@supabase/supabase-js';

export interface Profile {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: string | null;
  status: string | null;
  business_id: string | null;
  updated_at: string | null;
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: string | null;
  roleLabel: string;
  /** Kullanıcının aktif bir işletme üyeliği var mı (null = henüz belirlenmedi) */
  hasBusiness: boolean | null;
  /** Aktif işletmenin kayıtlı il/ilçesi — formlarda varsayılan öneri olarak kullanılır */
  businessCity: string | null;
  businessDistrict: string | null;
  /** Aktif işletmenin adı ve logosu — sidebar gibi yerlerde marka gösterimi için */
  businessName: string | null;
  businessLogoUrl: string | null;
  loading: boolean;
  error: string | null;
  /** Profil güncelle (ad soyad, telefon) */
  updateProfile: (updates: { full_name?: string; phone?: string; avatar_url?: string }) => Promise<{ success: boolean; error?: string }>;
  /** E-posta değiştir */
  updateEmail: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  /** Şifre değiştir */
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  /** Çıkış yap */
  signOut: () => Promise<{ success: boolean; error?: string }>;
  /** Profil bilgisini yeniden yükle */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  sales: 'Satış Personeli',
  accounting: 'Muhasebe',
  warehouse: 'Depo',
  delivery: 'Teslimat',
};

function getRoleLabel(role: string | null): string {
  if (!role) return 'Kullanıcı';
  return ROLE_LABELS[role] || role;
}

interface RoleResult {
  role: string | null;
  hasBusiness: boolean;
  businessCity: string | null;
  businessDistrict: string | null;
  businessName: string | null;
  businessLogoUrl: string | null;
}

const EMPTY_ROLE_RESULT: RoleResult = { role: null, hasBusiness: false, businessCity: null, businessDistrict: null, businessName: null, businessLogoUrl: null };

/** businessInfo state şekli — hasBusiness burada `null` da olabilir (henüz yüklenmedi/oturum kapalı). */
interface BusinessInfoState {
  hasBusiness: boolean | null;
  businessCity: string | null;
  businessDistrict: string | null;
  businessName: string | null;
  businessLogoUrl: string | null;
}
const EMPTY_BUSINESS_INFO: BusinessInfoState = { hasBusiness: null, businessCity: null, businessDistrict: null, businessName: null, businessLogoUrl: null };

function loadUserProfile(userId: string): Promise<{ profile: Profile | null } & RoleResult> {
  if (!supabase) return Promise.resolve({ profile: null, ...EMPTY_ROLE_RESULT });

  return supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()
    .then(({ data: profileData, error: profileError }) => {
      if (profileError) {
        console.error('loadProfile error:', profileError);
      }

      let profile: Profile | null = profileData as Profile | null;

      if (!profile) {
        return supabase.auth.getUser().then(({ data: authUser }) => {
          const email = authUser?.user?.email || '';
          const fullName = authUser?.user?.user_metadata?.full_name || '';

          const newProfile = {
            id: userId,
            full_name: fullName || email.split('@')[0] || 'Kullanıcı',
            email,
            phone: null,
            avatar_url: null,
            role: null,
            status: 'active',
          };

          return supabase
            .from('profiles')
            .insert([newProfile])
            .select('*')
            .single()
            .then(({ data: inserted, error: insertError }) => {
              if (insertError) {
                console.error('createProfile error:', insertError);
              }
              if (inserted) {
                profile = inserted as Profile;
              }
              return loadRole(userId).then((r) => ({ profile, ...r }));
            });
        });
      }

      return loadRole(userId).then((r) => ({ profile, ...r }));
    });
}

function loadRole(userId: string): Promise<RoleResult> {
  if (!supabase) return Promise.resolve(EMPTY_ROLE_RESULT);

  // .maybeSingle() burada kullanılmaz: kullanıcı ileride birden fazla
  // işletmeye üye olabilir (bkz. getUserBusinessId aynı gerekçe). Şimdilik
  // en eski aktif üyeliğin rolü esas alınır. businesses(...) embed'i,
  // işletmenin il/ilçe/ad/logo bilgisini (formlarda varsayılan öneri,
  // sidebar'da marka gösterimi için) tek sorguda çekiyor.
  return supabase
    .from('business_members')
    .select('role, businesses(name, city, district, logo_url)')
    .eq('user_id', userId)
    .eq('status', 'Aktif')
    .order('created_at', { ascending: true })
    .limit(1)
    .then(({ data, error }) => {
      if (error) {
        console.error('loadBusinessMember error:', error);
        return EMPTY_ROLE_RESULT;
      }
      const row = (data as any[] | null)?.[0];
      const biz = row?.businesses;
      return {
        role: row?.role || null,
        hasBusiness: !!row,
        businessCity: biz?.city || null,
        businessDistrict: biz?.district || null,
        businessName: biz?.name || null,
        businessLogoUrl: biz?.logo_url || null,
      };
    });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfoState>(EMPTY_BUSINESS_INFO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const result = await loadUserProfile(user.id);
      setProfile(result.profile);
      setRole(result.role);
      setBusinessInfo(result);
    } catch (err) {
      console.error('refreshProfile error:', err);
    }
  }, [user]);

  // İlk yükleme - getSession hemen, profile paralelde
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      setError('Supabase bağlantısı yapılandırılmamış.');
      return;
    }

    let mounted = true;

    // Bozuk tokenları temizle ve session'ı güvenli şekilde al
    const initAuth = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          // Session hatası varsa - token geçersiz, temizle
          console.warn('Auth init: session error, clearing tokens:', sessionError.message);
          await clearAllAuthState();
          if (mounted) setLoading(false);
          return;
        }

        if (!mounted) return;
        const currentSession = sessionData?.session || null;
        const currentUser = currentSession?.user || null;
        setSession(currentSession);
        setUser(currentUser);

        if (!currentUser) {
          setLoading(false);
          return;
        }

        // Profili paralelde yükle
        try {
          const result = await loadUserProfile(currentUser.id);
          if (mounted) {
            setProfile(result.profile);
            setRole(result.role);
            setBusinessInfo(result);
            setLoading(false);
          }
        } catch (profileErr) {
          console.warn('Auth init: profile load error:', profileErr);
          if (mounted) setLoading(false);
        }
      } catch (err: any) {
        console.warn('Auth init: unexpected error, clearing tokens:', err?.message || err);
        await clearAllAuthState();
        if (mounted) setLoading(false);
      }
    };

    // Tüm auth state'i agresif temizleme
    const clearAllAuthState = async () => {
      // localStorage'daki tüm supabase auth key'lerini önce temizle
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('supabase') || key.includes('sb-'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch {
        // localStorage erişilemezse boşver
      }
      // Best-effort: Supabase'in kendi signOut'unu dene
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // signOut başarısız olursa boşver, zaten localStorage'ı temizledik
      }
      clearBusinessIdCache();
      if (mounted) {
        setUser(null);
        setSession(null);
        setProfile(null);
        setRole(null);
        setBusinessInfo(EMPTY_BUSINESS_INFO);
        setError(null);
      }
    };

    initAuth();

    // Auth state değişikliklerini dinle - CALLBACK SYNC OLMALI, içeride async yok!
    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      // CRITICAL: Bu callback ASLA async olmamalı, Supabase lock deadlock yapar!
      const newUser = newSession?.user || null;

      if (event === 'SIGNED_OUT') {
        clearBusinessIdCache();
        setUser(null);
        setSession(null);
        setProfile(null);
        setRole(null);
        setBusinessInfo(EMPTY_BUSINESS_INFO);
        setError(null);
      } else if (event === 'USER_DELETED') {
        clearBusinessIdCache();
        setUser(null);
        setSession(null);
        setProfile(null);
        setRole(null);
        setBusinessInfo(EMPTY_BUSINESS_INFO);
        setError(null);
      } else if (newUser && (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION')) {
        setSession(newSession);
        setUser(newUser);
        // Non-blocking: .then() ile profil yükleme
        loadUserProfile(newUser.id).then((result) => {
          setProfile(result.profile);
          setRole(result.role);
          setBusinessInfo(result);
        }).catch((err) => {
          console.warn('onAuthStateChange profile load error:', err);
        });
      } else if (event === 'TOKEN_REFRESHED' && !newUser) {
        // Token yenileme başarısız olduysa session'ı temizle
        clearBusinessIdCache();
        setUser(null);
        setSession(null);
        setProfile(null);
        setRole(null);
        setBusinessInfo(EMPTY_BUSINESS_INFO);
      }
    });

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const updateProfile = useCallback(async (updates: { full_name?: string; phone?: string; avatar_url?: string }): Promise<{ success: boolean; error?: string }> => {
    if (!supabase || !user) return { success: false, error: 'Oturum bulunamadı.' };

    try {
      const updateData: Record<string, any> = { updated_at: new Date().toISOString() };
      if (updates.full_name !== undefined) updateData.full_name = updates.full_name;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.avatar_url !== undefined) updateData.avatar_url = updates.avatar_url;

      const { data, error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select('*')
        .single();

      if (updateError) {
        console.error('updateProfile error:', updateError);
        return { success: false, error: 'Profil bilgileri güncellenemedi.' };
      }

      if (data) {
        setProfile(data as Profile);
      }

      // Auth metadata da güncelle
      if (updates.full_name !== undefined) {
        await supabase.auth.updateUser({ data: { full_name: updates.full_name } });
      }

      return { success: true };
    } catch (err: any) {
      console.error('updateProfile error:', err);
      return { success: false, error: 'Profil bilgileri güncellenemedi.' };
    }
  }, [user]);

  const updateEmail = useCallback(async (newEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!supabase || !user) return { success: false, error: 'Oturum bulunamadı.' };

    try {
      const { error: emailError } = await supabase.auth.updateUser({ email: newEmail });

      if (emailError) {
        console.error('updateEmail error:', emailError);
        return { success: false, error: 'E-posta güncellenemedi: ' + emailError.message };
      }

      return { success: true };
    } catch (err: any) {
      console.error('updateEmail error:', err);
      return { success: false, error: 'E-posta güncellenemedi.' };
    }
  }, [user]);

  const updatePassword = useCallback(async (currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!supabase || !user) return { success: false, error: 'Oturum bulunamadı.' };

    try {
      // Önce mevcut şifreyi doğrula
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword,
      });

      if (signInError) {
        return { success: false, error: 'Mevcut şifreniz hatalı.' };
      }

      // Yeni şifreyi kaydet
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });

      if (updateError) {
        console.error('updatePassword error:', updateError);
        return { success: false, error: 'Şifre güncellenemedi: ' + updateError.message };
      }

      return { success: true };
    } catch (err: any) {
      console.error('updatePassword error:', err);
      return { success: false, error: 'Şifre güncellenemedi.' };
    }
  }, [user]);

  const signOut = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!supabase) return { success: false, error: 'Supabase bağlantısı yok.' };

    // Önce localStorage'daki tüm supabase token'larını temizle
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      // localStorage erişilemezse boşver
    }

    // Best-effort: Session varsa sunucu tarafında da çıkış yap
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // AuthSessionMissingError veya başka hata - session zaten yok, sessizce yut
    }

    // Aktif işletme cache'ini temizle — aksi halde aynı sekmede farklı bir
    // kullanıcı giriş yaparsa önceki kullanıcının business_id'si sızabilir.
    clearBusinessIdCache();

    // Her durumda React state'i temizle
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
    setBusinessInfo(EMPTY_BUSINESS_INFO);
    return { success: true };
  }, []);

  const value: AuthState = {
    user,
    session,
    profile,
    role,
    roleLabel: getRoleLabel(role),
    ...businessInfo,
    loading,
    error,
    updateProfile,
    updateEmail,
    updatePassword,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}